import { useEffect, useState } from 'react';

import type { AuthApi, DeviceSession, Session } from './auth/api';
import { errorKey } from './auth/api';
import { useI18n } from './i18n/I18nProvider';
import { LOCALES, LOCALE_NAME, type MessageKey } from './i18n/i18n';
import { SKINS, MODES } from './theme/theme';
import { useTheme } from './theme/ThemeProvider';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { Sheet } from './ui/Sheet';
import './settings.css';

export function SettingsSheet(
  { open, api, session, onClose, onLogout }:
  {
    open: boolean;
    api: AuthApi;
    session: Session | null;
    onClose(): void;
    onLogout(): void;
  },
) {
  const { skin, mode, setSkin, setMode } = useTheme();
  const { t, i18n, locale, setLocale } = useI18n();
  const [devices, setDevices] = useState<DeviceSession[] | null>(null);
  const [err, setErr] = useState('');

  // 设备列表只在打开时拉一次 —— 它不是实时数据，没必要轮询
  useEffect(() => {
    if (!open || !session) { setDevices(null); return; }
    let alive = true;
    api.sessions(session.token)
      .then((r) => { if (alive) setDevices(r.sessions); })
      .catch((e) => { if (alive) setErr(t(errorKey(e))); });
    return () => { alive = false; };
  }, [open, session, api, t]);

  return (
    <Sheet open={open} title={t('settings.title')} onClose={onClose}>
      {/* 语言放最前面 —— 一个看不懂当前语言的人，**要能先找到这个** */}
      <section className="stack">
        <h3 className="h2 row"><Icon name="globe" className="set__icon" />{t('settings.language')}</h3>
        <div className="chips">
          {LOCALES.map((l) => (
            <button key={l} type="button" className="chip"
                    aria-pressed={l === locale} onClick={() => setLocale(l)}
                    lang={l}>
              {/* 语言名**永远用母语写** —— 只看得懂葡语的人在中文界面里
                  要能认出 "Português"，写成"葡萄牙语"他就找不到 */}
              {LOCALE_NAME[l]}
            </button>
          ))}
        </div>
      </section>

      <section className="stack">
        <h3 className="h2 row"><Icon name="palette" className="set__icon" />{t('settings.skin')}</h3>
        {/* 换肤是**改一个 data 属性**，不重挂组件、不重载 CSS、没有闪白 */}
        <div className="chips">
          {SKINS.map((s) => (
            <button key={s} type="button" className="chip"
                    aria-pressed={s === skin} onClick={() => setSkin(s)}>
              <span className={`chip__swatch chip__swatch--${s}`} aria-hidden="true" />
              {t(`skin.${s}` as MessageKey)}
            </button>
          ))}
        </div>
      </section>

      <section className="stack">
        <h3 className="h2 row"><Icon name="sun" className="set__icon" />{t('settings.mode')}</h3>
        <div className="chips">
          {MODES.map((m) => (
            <button key={m} type="button" className="chip"
                    aria-pressed={m === mode} onClick={() => setMode(m)}>
              {t(`mode.${m}` as MessageKey)}
            </button>
          ))}
        </div>
      </section>

      <section className="stack">
        <h3 className="h2 row"><Icon name="user" className="set__icon" />{t('settings.account')}</h3>
        {!session ? (
          <p className="small dim set__note">{t('settings.notLoggedIn')}</p>
        ) : (
          <>
            <div className="card stack">
              <div className="spread">
                <span className="small dim">{t('settings.playerId')}</span>
                <span className="small num">{session.uid}</span>
              </div>
            </div>

            <h4 className="small dim set__sub">{t('settings.devices')}</h4>
            {err && <p className="field__err" role="alert">{err}</p>}
            {devices === null && !err && (
              <p className="small dim set__note">{t('settings.loading')}</p>)}
            {devices?.map((d) => (
              <div key={d.id} className="card device">
                <div className="spread">
                  <span className="h2">{d.device ?? t('settings.unknownDevice')}</span>
                  {d.current && <span className="badge">{t('settings.current')}</span>}
                </div>
                <span className="small dim">
                  {d.ip ?? '—'} · {i18n.idle(d.idleSeconds)}
                </span>
              </div>
            ))}

            <div className="row set__actions">
              <Button variant="ghost" onClick={() => {
                if (!session) return;
                api.revokeOthers(session.token)
                  .then(() => api.sessions(session.token))
                  .then((r) => setDevices(r.sessions))
                  .catch((e) => setErr(t(errorKey(e))));
              }}>
                {t('settings.revokeOthers')}
              </Button>
              <Button variant="ghost" onClick={onLogout}>{t('settings.logout')}</Button>
            </div>
          </>
        )}
      </section>
    </Sheet>
  );
}
